import { dungeonService } from './DungeonService';
import {
  IAffectedTilePreview,
  ICreateAffectedTileOptions,
} from '../types/types';
import { v4 as uuidv4 } from 'uuid';

export class PreviewService {
  public dungeonService!: typeof dungeonService;
  private affectedTiles: IAffectedTilePreview[] = [];
  // Client only: maps a predicted cast's castSeq -> the local groupId it created,
  // so a host preview-add/cast-rejected can reconcile/remove the right telegraph.
  private predictedByCastSeq = new Map<number, string>();

  public createAffectedTile(
    x: number,
    y: number,
    options?: ICreateAffectedTileOptions,
  ): string {
    const tile: IAffectedTilePreview = {
      id: uuidv4(),
      x,
      y,
      entityId: options?.entityId,
      duration: options?.duration,
      elapsedTime: 0,
      type: options?.type || 'default',
      color: options?.color ?? 0xff0000,
      alpha: options?.alpha ?? 0.3,
      groupId: options?.groupId,
      isPlayer: options?.isPlayer,
      noProgressFill: options?.noProgressFill,
    };
    this.affectedTiles.push(tile);
    this.dungeonService.markNeedsRender();
    return tile.id;
  }

  public removeAffectedTile(id: string): void {
    const index = this.affectedTiles.findIndex((t) => t.id === id);
    if (index !== -1) {
      this.affectedTiles.splice(index, 1);
      this.dungeonService.markNeedsRender();
    }
  }

  public getAffectedTiles(): IAffectedTilePreview[] {
    return this.affectedTiles;
  }

  public getAffectedTilesByEntity(entityId: string): IAffectedTilePreview[] {
    return this.affectedTiles.filter((t) => t.entityId === entityId);
  }

  public removeAffectedTilesByEntity(entityId: string): void {
    this.affectedTiles
      .filter((t) => t.entityId === entityId)
      .forEach((t) => this.removeAffectedTile(t.id));
  }

  public getAffectedTilesByGroup(groupId: string): IAffectedTilePreview[] {
    return this.affectedTiles.filter((t) => t.groupId === groupId);
  }

  public removeAffectedTilesByGroup(groupId: string): void {
    this.affectedTiles
      .filter((t) => t.groupId === groupId)
      .forEach((t) => this.removeAffectedTile(t.id));
  }

  // A group's tiles share one elapsedTime; returns it (or undefined if absent).
  // Lets the client keep the local timer when reconciling onto host cells.
  public getGroupElapsed(groupId: string): number | undefined {
    return this.affectedTiles.find((t) => t.groupId === groupId)?.elapsedTime;
  }

  public recordPredictedGroup(castSeq: number, groupId: string): void {
    this.predictedByCastSeq.set(castSeq, groupId);
  }

  // Paired with the castSeq counter reset (resetMoveTracking): a revive restarts
  // castSeq at 0, so any in-flight predicted entry would collide with a future seq.
  public clearPredictions(): void {
    this.predictedByCastSeq.clear();
  }

  public takePredictedGroup(castSeq: number): string | undefined {
    const groupId = this.predictedByCastSeq.get(castSeq);
    this.predictedByCastSeq.delete(castSeq);
    return groupId;
  }

  public updateAffectedTiles(deltaTime: number): void {
    const expired: string[] = [];
    for (const tile of this.affectedTiles) {
      if (tile.duration !== undefined && tile.elapsedTime !== undefined) {
        tile.elapsedTime += deltaTime;
        if (tile.elapsedTime >= tile.duration) {
          expired.push(tile.id);
        }
      }
    }
    expired.forEach((id) => this.removeAffectedTile(id));
  }

  public clearAffectedTiles(): void {
    this.affectedTiles = [];
    this.predictedByCastSeq.clear();
    this.dungeonService.markNeedsRender();
  }
}
